import { useMemo } from 'react'
import { getYears } from '@/utils/vehicle'
import GenerationYearSelect from './generationYearSelect'
import moment from 'moment'
import { useRouter } from 'next/navigation'
import qs from 'qs'
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Grid, Loader, Pagination, Row, Panel } from 'rsuite'
import SaveToLocalCheckbox from '@/components/shared/saveToLocalCheckbox'
import PublicationResultHeader from '@/components/shared/publicationResultHeader'
import SelectedCriteriaPane from '@/components/shared/selectedCriteriaPane'
import { ContentContainer, PublicationRow } from '@/components/styles'
import { configSelectors } from '@/store/configs'
import { searchActions, searchSelectors } from '@/store/search'
import useTranslation from '@/hooks/useTranslation'
import FooterMessage from '@/components/shared/footerMessage'
import VideoModal from '@/components/videoModal'
import useVideoModal from '@/hooks/useVideoModal'
import ContentHeader from '@/components/shared/contentHeader'
import { i18nL10nSelectors } from '@/store/i18nL10n'
import { loggedInUserSelector } from '@/store/loggedInUser'
import { getPublications, sortPublicationsInOrderToPriority } from '@/utils/publications/helpers'
import { generalConstants, i18nConstants } from '@tweddle-se-team/common/constants'
import { generalHelpers } from '@tweddle-se-team/common/helpers'
import styles from './publications.module.scss'
import cn from 'classnames'
import SearchByKeyword from './searchByKeyword'
import { showWarning } from '../shared/toaster'
import { getPublicationUiKey, mixServiceWarrantyPublicationForYear } from '@/utils/publications/serviceWarranty'

const { dateFormat, DFOM, OM, WTY } = generalConstants
const { rtlLanguagesISO2Codes } = i18nConstants
const { withLoading } = generalHelpers

const update = async (filters, pagination, updatePagination, setSearchResults) => {
  updatePagination(pagination)
  const publications = await getPublications(filters, pagination)
  setSearchResults(publications)
}

const mapPublicationForDisplay = ({
  _id,
  partNumber,
  publicationType,
  language,
  brand,
  model,
  modelType,
  year,
  lineOffDate,
  contents
}) => ({
  _id,
  partNumber,
  language,
  brand,
  model,
  modelType,
  year,
  publicationType,
  lineOffDate: lineOffDate && moment.utc(lineOffDate).format(dateFormat),
  contents: {
    ditaId: contents.ditaId,
    webview: contents.webview
  }
})

const Publications = ({ availableYears, productionDate, katashiki, serviceWarrantyPublications = [] }) => {
  // <Redux>
  const dispatch = useDispatch()
  const { isInFrame, isInApp } = useSelector(configSelectors.selectConfigs)
  const publicationTypes = useSelector(configSelectors.selectConfigByKey('publicationTypes'))
  const { filterBy, pagination } = useSelector(searchSelectors.selectQuery)
  const { publications } = useSelector(searchSelectors.selectResults)
  const {
    pane: { generation }
  } = useSelector(searchSelectors.selectSearch)
  const locale = useSelector(i18nL10nSelectors.selectLocale)
  const user = useSelector(loggedInUserSelector.selectUser)
  // </Redux>
  const [loading, setLoading] = useState(false)
  const { videoTitle, videoUrl, openVideoModal, closeVideoModal, isVideoModalOpen } = useVideoModal()
  const t = useTranslation()
  const router = useRouter()
  const [showSearchModal, setShowSearchModal] = useState(false)
  const { items = [], total } = publications
  const { page, limit } = pagination
  const videoContent = items
    .filter(i => i.publicationType === 'VIDEO')
    .map(({ contents }) => contents)
    .flat()
  let data = items
    .filter(i => i.publicationType !== 'VIDEO' && i.publicationType !== DFOM)
    .map(mapPublicationForDisplay)
    .filter(i => i.contents.ditaId || i.contents.webview)
  const serviceWarrantyData = serviceWarrantyPublications
    .map(mapPublicationForDisplay)
    .filter(i => i.contents.ditaId || i.contents.webview)

  // If no year is selected filterBy.year is an array with all the available years
  // For search if only one year is available, use that year
  const yearForSearch = useMemo(() => {
    let year
    if (Array.isArray(filterBy.year)) {
      if (filterBy.year.length === 1) {
        year = filterBy.year[0]
      } else if (availableYears.length === 1) {
        year = availableYears[0]
      }
    } else if (!isNaN(filterBy.year)) {
      year = filterBy.year
    }
    return year
  }, [filterBy.year, availableYears])

  const setSearchResults = useCallback(
    publications => dispatch(searchActions.SET_PUBLICATIONS_RESULT(publications)),
    [dispatch]
  )
  const updatePagination = useCallback(pagination => dispatch(searchActions.UPDATE_PAGINATION(pagination)), [dispatch])

  const changePage = async (page = 1) => {
    await withLoading({ setLoading })(async () => {
      const newPagination = { ...pagination, page }
      await update(filterBy, newPagination, updatePagination, setSearchResults)
    })()
  }
  const selectYear = (generation, year) => {
    setShowSearchModal(false)
    router.push(`/publications?${qs.stringify({ ...filterBy, year: year || getYears(generation) })}`)
  }

  if (katashiki && productionDate) {
    if (page !== 1) {
      data = data.filter(publication => publication.publicationType !== WTY)
    } else {
      const prioritizedPublications = sortPublicationsInOrderToPriority(
        data.filter(publication => publication.publicationType !== WTY),
        productionDate
      )
      const targetPublication =
        prioritizedPublications.find(publication => publication.publicationType === OM) ||
        prioritizedPublications.find(publication => publication.year)

      data = mixServiceWarrantyPublicationForYear({
        publications: prioritizedPublications,
        serviceWarrantyPublications: serviceWarrantyData,
        targetYear: targetPublication?.year
      })
    }
  } else if (!Array.isArray(filterBy.year)) {
    if (page !== 1) {
      data = data.filter(publication => publication.publicationType !== WTY)
    } else {
      data = mixServiceWarrantyPublicationForYear({
        publications: data,
        serviceWarrantyPublications: serviceWarrantyData,
        targetYear: filterBy.year
      })
    }
  }

  const onSearchButtonClick = useCallback(() => {
    if (showSearchModal) {
      setShowSearchModal(false)
      return
    }

    if (!isNaN(yearForSearch) || katashiki) {
      setShowSearchModal(true)
    } else {
      return showWarning(t('search_select_vehicle_year', 'Select a vehicle year to search by keyword'))
    }
  }, [setShowSearchModal, yearForSearch, t, showSearchModal, katashiki])

  return (
    <ContentContainer $rightAlign={rtlLanguagesISO2Codes.includes(locale)}>
      <ContentHeader
        title={`${filterBy.modelType} ${t('digital_first_topic_type_manual', 'Manuals')}`}
        paragraph
        rtl={rtlLanguagesISO2Codes.includes(locale)}
        useHeaderTag
      />
      <Grid fluid>
        <Row gutter={50}>
          <Col md={6}>
            <SelectedCriteriaPane />
          </Col>
          <Col md={18} className={cn(['mt-20', styles.publicationsPanel])}>
            <GenerationYearSelect
              generation={generation}
              availableYears={availableYears}
              select={selectYear}
              value={filterBy.year}
              rtl={rtlLanguagesISO2Codes.includes(locale)}
              katashiki={katashiki}
              onSearchButtonClick={onSearchButtonClick}
              isSearchOpen={showSearchModal}
            />
            {!data.length && <p>{t('finding_vehicle_publications_no_pubs')}.</p>}
            {showSearchModal && (
              <SearchByKeyword
                brand={filterBy.brand}
                model={filterBy.model}
                modelType={filterBy.modelType}
                year={yearForSearch}
                language={locale}
                closeWrapper={() => setShowSearchModal(false)}
                fullReloadOnSelectTopic={false}
              />
            )}
            {!showSearchModal && (
              <>
                <Panel
                  header={`${t('digital_first_topic_type_manual')} (${data.length})`}
                  collapsible
                  defaultExpanded
                  className={styles.publicationsPanel}
                >
                  {data.map(item => (
                    <PublicationRow className="mb-20" key={getPublicationUiKey(item)}>
                      <PublicationResultHeader
                        publicationTypes={publicationTypes}
                        publication={item}
                        user={user}
                        showDownloadOption={!item.contents?.ditaId}
                        cached={false}
                      />
                      {!isInFrame && <SaveToLocalCheckbox publication={item} />}
                    </PublicationRow>
                  ))}
                </Panel>
                <Panel
                  header={`${t('digital_first_topic_type_video')} (${videoContent.length})`}
                  collapsible
                  defaultExpanded
                  className={styles.publicationsPanel}
                >
                  {videoContent.map(content => (
                    <PublicationRow className="mb-20" key={content._id}>
                      <span
                        className="cursorpointer"
                        onClick={() => openVideoModal(content.title, content.url)}
                        tabIndex={0}
                        role="button"
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            openVideoModal(content.title, content.url)
                          }
                        }}
                      >
                        <b style={{ fontSize: 16 }}>{content.title}</b>
                      </span>
                    </PublicationRow>
                  ))}
                  {videoContent.length < 1 && (
                    <span className={styles.notFound}>{t('digital_first_topic_type_no_content')}</span>
                  )}
                </Panel>
                {total > limit && (
                  <div className="flex-c">
                    <Pagination
                      prev
                      last
                      next
                      first
                      size="lg"
                      pages={Math.ceil((total || 1) / limit)}
                      activePage={page}
                      onSelect={changePage}
                      limit={limit}
                    />
                  </div>
                )}
                {!isInApp && <FooterMessage brand={filterBy.brand} />}
              </>
            )}
          </Col>
        </Row>
      </Grid>
      {loading && <Loader backdrop center vertical size="lg" />}
      <VideoModal show={isVideoModalOpen} close={closeVideoModal} url={videoUrl} title={videoTitle} />
    </ContentContainer>
  )
}

export default Publications
